import { memo, forwardRef, useCallback } from 'react';
import { CircularProgress } from '@mui/material';
import { ButtonComponentProps } from './Button.types';
import { StyledButton } from './Button.styles';

/**
 * Button Component
 *
 * Professional button component following the green/teal design system.
 * Fully memoized for optimal performance in large ERP applications.
 *
 * @example
 * ```tsx
 * // Primary button
 * <Button variant="primary" onClick={handleSave}>
 *   Save Changes
 * </Button>
 *
 * // With loading state
 * <Button variant="primary" loading={isSubmitting}>
 *   Submit Order
 * </Button>
 *
 * // With icons
 * <Button variant="primary" startIcon={<AddIcon />}>
 *   Add New
 * </Button>
 * ```
 */
export const Button = memo(
  forwardRef<HTMLButtonElement, ButtonComponentProps>(
    function Button(
      {
        variant = 'primary',
        size = 'md',
        loading = false,
        loadingText,
        disabled = false,
        children,
        startIcon,
        endIcon,
        onClick,
        type = 'button',
        fullWidth = false,
        ...props
      },
      ref
    ) {
      // Memoize click handler to prevent unnecessary re-renders
      const handleClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
          if (!loading && !disabled && onClick) {
            onClick(event);
          }
        },
        [loading, disabled, onClick]
      );

      // Determine button content
      const buttonContent = loading && loadingText ? loadingText : children;

      // Determine start icon (replace with spinner if loading)
      const displayStartIcon = loading ? (
        <CircularProgress size={16} color="inherit" />
      ) : (
        startIcon
      );

      // Don't show end icon when loading
      const displayEndIcon = loading ? undefined : endIcon;

      return (
        <StyledButton
          ref={ref}
          buttonVariant={variant}
          buttonSize={size}
          disabled={disabled || loading}
          onClick={handleClick}
          type={type}
          fullWidth={fullWidth}
          startIcon={displayStartIcon}
          endIcon={displayEndIcon}
          {...props}
        >
          {buttonContent}
        </StyledButton>
      );
    }
  )
);

Button.displayName = 'Button';

export default Button;
